import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';

const SERIF = "'Bodoni Moda', Georgia, serif";
const MONO  = "'IBM Plex Mono', monospace";

const Section = ({ id, title, children }) => (
  <section id={id} className="space-y-4">
    <h2 className="text-xl font-semibold text-foreground border-b border-border pb-3">{title}</h2>
    <div className="space-y-3 text-muted-foreground leading-relaxed text-sm">{children}</div>
  </section>
);

const Privacy = () => {
  const lastUpdated = 'February 2026';

  return (
    <div className="min-h-screen bg-white py-20 px-6 lg:px-20">
      <SEO
        title="Privacy Policy — FashionSpyder"
        description="FashionSpyder's privacy policy. Understand how we collect, use and protect your personal data. GDPR compliant. Data controller: Influentive Ltd, Malta."
        path="/privacy"
      />
      <div className="max-w-3xl mx-auto">

        {/* Header */}
        <div className="mb-14">
          <span className="text-xs uppercase tracking-widest text-muted-foreground" style={{ fontFamily: MONO }}>
            Legal · GDPR Compliant
          </span>
          <h1 className="text-4xl lg:text-5xl font-semibold mt-4 mb-3 tracking-tight" style={{ fontFamily: SERIF }}>
            Privacy Policy
          </h1>
          <p className="text-sm text-muted-foreground" style={{ fontFamily: MONO }}>
            Last updated: {lastUpdated}
          </p>
        </div>

        <div className="space-y-12">

          <Section title="1. Who We Are">
            <p>
              FashionSpyder is a service developed under the <strong className="text-foreground">Flence ecosystem</strong>,
              owned and operated by <strong className="text-foreground">Influentive Ltd, Malta</strong>. FashionSpyder is an AI-powered HR intelligence platform that connects
              fashion brand HR teams with verified creative professionals sourced through Flence.
            </p>
            <p>
              For the purposes of data protection law, <strong className="text-foreground">Influentive Ltd</strong> (trading as Flence / FashionSpyder)
              acts as the <strong className="text-foreground">data controller</strong> in respect of the personal data processed through this platform.
            </p>
            <p>
              Contact for data protection matters:{' '}
              <a href="mailto:privacy@fashionspyder.com" className="text-foreground underline underline-offset-2">
                privacy@fashionspyder.com
              </a>
            </p>
          </Section>

          <Section title="2. Data We Collect">
            <p>We collect and process the following categories of personal data:</p>
            <div className="space-y-4">
              <div className="border border-border p-4">
                <p className="font-semibold text-foreground mb-1.5">HR Users (platform subscribers)</p>
                <ul className="list-disc pl-4 space-y-1">
                  <li>Identity: full name, job title</li>
                  <li>Contact: work email address, company name</li>
                  <li>Account: hashed password, session tokens, 2FA codes</li>
                  <li>Payment: subscription plan, billing status (payment details handled by Stripe — we do not store card numbers)</li>
                  <li>Usage: search queries, match history, pipeline activity, viewed profiles</li>
                  <li>Technical: IP address, browser type, device information, access logs</li>
                </ul>
              </div>
              <div className="border border-border p-4">
                <p className="font-semibold text-foreground mb-1.5">Fashion Professionals (candidate profiles)</p>
                <ul className="list-disc pl-4 space-y-1">
                  <li>Profile data received via the Flence platform webhook (name, photo, role, skills, portfolio, location, availability)</li>
                  <li>FashionSpyder does not directly collect data from candidates — all candidate data originates from Flence, where professionals have agreed to Flence's own terms and privacy policy</li>
                  <li>Candidates may request removal of their profile by contacting Flence or emailing us at <a href="mailto:privacy@fashionspyder.com" className="underline underline-offset-2">privacy@fashionspyder.com</a></li>
                </ul>
              </div>
              <div className="border border-border p-4">
                <p className="font-semibold text-foreground mb-1.5">Waitlist Entries</p>
                <ul className="list-disc pl-4 space-y-1">
                  <li>Name, work email, company name, role type</li>
                  <li>Used solely to notify you at launch and manage access eligibility</li>
                </ul>
              </div>
            </div>
          </Section>

          <Section title="3. Legal Basis for Processing (GDPR Art. 6)">
            <ul className="list-disc pl-4 space-y-2">
              <li><strong className="text-foreground">Contract performance (Art. 6(1)(b)):</strong> Processing necessary to provide the platform service to subscribers — account management, authentication, AI matching, pipeline tools.</li>
              <li><strong className="text-foreground">Legitimate interests (Art. 6(1)(f)):</strong> Platform security, fraud prevention, service improvement, usage analytics.</li>
              <li><strong className="text-foreground">Consent (Art. 6(1)(a)):</strong> Optional/analytics cookies, marketing communications (where separately opted in).</li>
              <li><strong className="text-foreground">Legal obligation (Art. 6(1)(c)):</strong> Tax records, financial transaction logs (Stripe).</li>
            </ul>
          </Section>

          <Section title="4. How We Use Your Data">
            <ul className="list-disc pl-4 space-y-2">
              <li>Providing and operating the FashionSpyder platform (gallery, AI Matchmaker, pipeline)</li>
              <li>Authenticating users and maintaining secure sessions</li>
              <li>Processing subscription payments via Stripe</li>
              <li>Running the AI Matchmaker algorithm (candidate data is processed by OpenAI GPT via the Emergent integrations layer — no candidate PII is used for model training)</li>
              <li>Sending transactional emails: OTP codes, password resets, subscription notifications (via Resend)</li>
              <li>Improving platform features through anonymous usage analytics</li>
              <li>Marketing and advertising on Meta and Pinterest (with your cookie consent)</li>
              <li>Preventing fraud, abuse, and unauthorised access</li>
              <li>Complying with legal obligations</li>
            </ul>
          </Section>

          <Section title="5. Third-Party Processors">
            <p>We share your data only with trusted processors under appropriate data processing agreements:</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border border-border">
                <thead>
                  <tr className="bg-secondary">
                    <th className="text-left px-3 py-2 border-b border-border font-semibold text-foreground">Processor</th>
                    <th className="text-left px-3 py-2 border-b border-border font-semibold text-foreground">Purpose</th>
                    <th className="text-left px-3 py-2 border-b border-border font-semibold text-foreground">Location</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {[
                    ['Supabase',              'Database hosting (PostgreSQL + pgvector)',   'EU (AWS Frankfurt)'],
                    ['Stripe',                'Payment processing',                          'USA (SCCs apply)'],
                    ['Resend',                'Transactional email delivery',                'USA (SCCs apply)'],
                    ['OpenAI via Emergent',   'AI matching & text generation',              'USA (SCCs apply)'],
                    ['Google Analytics 4',    'Aggregated usage analytics (IP anonymised)', 'USA (SCCs apply)'],
                    ['Meta (Facebook Pixel)', 'Conversion tracking & ad optimisation (with consent)',  'USA (SCCs apply)'],
                    ['Pinterest Tag',          'Conversion tracking & trend audience research (with consent)', 'USA (SCCs apply)'],
                    ['Flence',                'Candidate profile data source',               'EU'],
                  ].map(([name, purpose, location]) => (
                    <tr key={name}>
                      <td className="px-3 py-2 font-medium text-foreground">{name}</td>
                      <td className="px-3 py-2">{purpose}</td>
                      <td className="px-3 py-2">{location}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs">
              Where data is transferred outside the EEA, we rely on Standard Contractual Clauses (SCCs) approved by the European Commission.
            </p>
          </Section>

          <Section title="6. Data Retention">
            <ul className="list-disc pl-4 space-y-2">
              <li><strong className="text-foreground">HR user accounts:</strong> Retained for the duration of the subscription plus 2 years, then deleted unless required by law.</li>
              <li><strong className="text-foreground">Candidate profiles:</strong> Retained while the candidate maintains an active Flence profile. Profiles are removed within 30 days of deletion from Flence.</li>
              <li><strong className="text-foreground">Waitlist entries:</strong> Retained until the platform launches and invitations are sent, or until you request deletion.</li>
              <li><strong className="text-foreground">Payment records:</strong> Retained for 7 years to comply with Italian and EU tax law.</li>
              <li><strong className="text-foreground">Access logs:</strong> Retained for 90 days for security purposes.</li>
            </ul>
          </Section>

          <Section title="7. Cookies">
            <p>
              We use cookies and similar technologies. See our full{' '}
              <Link to="/cookies" className="text-foreground underline underline-offset-2">Cookie Policy</Link>{' '}
              for details. You can manage your preferences at any time via the consent banner.
            </p>
          </Section>

          <Section title="8. Your Rights Under GDPR">
            <p>As a data subject in the EU/EEA, you have the following rights:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                ['Right of access', 'Request a copy of all personal data we hold about you.'],
                ['Right to rectification', 'Request correction of inaccurate or incomplete data.'],
                ['Right to erasure', 'Request deletion of your personal data ("right to be forgotten").'],
                ['Right to restriction', 'Request that we limit how we use your data.'],
                ['Right to portability', 'Receive your data in a machine-readable format.'],
                ['Right to object', 'Object to processing based on legitimate interests or direct marketing.'],
                ['Right to withdraw consent', 'Withdraw any previously given consent at any time.'],
                ['Right to lodge a complaint', 'Contact your national supervisory authority (in Italy: Garante Privacy — www.garanteprivacy.it).'],
              ].map(([right, desc]) => (
                <div key={right} className="border border-border p-3">
                  <p className="font-semibold text-foreground text-xs mb-1">{right}</p>
                  <p className="text-xs">{desc}</p>
                </div>
              ))}
            </div>
            <p>
              To exercise any of these rights, email{' '}
              <a href="mailto:privacy@fashionspyder.com" className="text-foreground underline underline-offset-2">
                privacy@fashionspyder.com
              </a>.
              We will respond within 30 days.
            </p>
          </Section>

          <Section title="9. Security">
            <p>
              We implement appropriate technical and organisational measures to protect your personal data against
              unauthorised access, loss, or disclosure. These include:
            </p>
            <ul className="list-disc pl-4 space-y-1">
              <li>All data transmitted over HTTPS/TLS encryption</li>
              <li>Passwords stored using bcrypt hashing — never in plain text</li>
              <li>Authentication sessions managed via secure, httpOnly cookies</li>
              <li>Two-factor authentication (2FA) available for all accounts</li>
              <li>Database hosted on SOC 2-certified infrastructure (Supabase / AWS)</li>
              <li>Access to production systems restricted to the development team</li>
            </ul>
          </Section>

          <Section title="10. Children">
            <p>
              FashionSpyder is a professional B2B platform intended exclusively for HR professionals and is not directed
              at or intended for use by anyone under the age of 18. We do not knowingly collect personal data from minors.
            </p>
          </Section>

          <Section title="11. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. We will notify active subscribers of material changes
              by email. The "Last updated" date at the top of this page will always reflect the most recent revision.
              Continued use of the platform after changes constitutes acceptance of the updated policy.
            </p>
          </Section>

          <Section title="12. Contact">
            <p>
              For any privacy-related questions, requests, or complaints:<br />
              <strong className="text-foreground">FashionSpyder / Flence</strong><br />
              Milan, Italy<br />
              Email:{' '}
              <a href="mailto:privacy@fashionspyder.com" className="text-foreground underline underline-offset-2">
                privacy@fashionspyder.com
              </a>
            </p>
          </Section>

        </div>
      </div>
    </div>
  );
};

export default Privacy;
